import React, { forwardRef, useImperativeHandle, useState } from 'react';
import AddressFields, { AddressFieldsValue } from './AddressFields';

export interface AddressOption {
  id: number;
  street: string;
  city: string;
  state: string;
  zip: string;
}

export interface AddressPickerHandle {
  // Resolves the address id the parent's own Save button should use: if the
  // user was mid-way through "+ New address", this creates it first.
  commit: () => Promise<number | ''>;
}

interface AddressPickerProps {
  label: string;
  addresses: AddressOption[];
  value: number | '';
  onChange: (id: number | '') => void;
  onCreateAddress: (fields: AddressFieldsValue) => Promise<AddressOption>;
  disabled?: boolean;
  optional?: boolean;
}

const selectStyle: React.CSSProperties = {
  padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '6px',
  fontSize: '14px', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box',
};
const labelStyle: React.CSSProperties = { fontSize: '13px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 };

const NEW_ADDRESS_VALUE = '__new__';
const BLANK_FIELDS: AddressFieldsValue = { street: '', city: '', state: '', zip: '' };

const AddressPicker = forwardRef<AddressPickerHandle, AddressPickerProps>(
  ({ label, addresses, value, onChange, onCreateAddress, disabled, optional = true }, ref) => {
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [fields, setFields] = useState<AddressFieldsValue>(BLANK_FIELDS);

    useImperativeHandle(ref, () => ({
      commit: async () => {
        if (!creating) return value;
        if (!fields.street || !fields.city || !fields.state || !fields.zip) {
          setError('Please fill in all address fields');
          throw new Error('Please fill in all address fields');
        }
        const created = await onCreateAddress(fields);
        onChange(created.id);
        setCreating(false);
        setFields(BLANK_FIELDS);
        return created.id;
      },
    }));

    const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const v = e.target.value;
      if (v === NEW_ADDRESS_VALUE) {
        setCreating(true);
        return;
      }
      onChange(v ? parseInt(v, 10) : '');
    };

    return (
      <div>
        <label style={labelStyle}>{label}</label>
        {!creating ? (
          <select style={selectStyle} value={value} disabled={disabled} onChange={handleSelectChange}>
            {optional && <option value="">No address</option>}
            {addresses.map((addr) => (
              <option key={addr.id} value={addr.id}>
                {addr.street}, {addr.city}, {addr.state} {addr.zip}
              </option>
            ))}
            <option value={NEW_ADDRESS_VALUE}>+ New address...</option>
          </select>
        ) : (
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: 12, marginTop: 4 }}>
            {error && (
              <div style={{ padding: 8, background: '#fee2e2', color: '#dc2626', borderRadius: 4, marginBottom: 8, fontSize: 13 }}>
                {error}
              </div>
            )}
            <AddressFields value={fields} onChange={setFields} disabled={disabled} />
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
              This address will be saved when you click Save below.
            </div>
            <button
              type="button"
              onClick={() => { setCreating(false); setError(null); setFields(BLANK_FIELDS); }}
              disabled={disabled}
              style={{ padding: '8px 16px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 13 }}
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    );
  }
);

export default AddressPicker;
